import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { useImpersonation } from "@/contexts/ImpersonationContext";
import type { SrIconName } from "@/components/ui/SrIcon";

export interface SidebarShortcut {
  key: string;
  label: string;
  path: string;
  icon: SrIconName;
}

export interface DBSidebarShortcut {
  id: string;
  role: string;
  label: string;
  path: string;
  icon: string;
  sort_order: number;
  enabled: boolean;
  created_at: string;
  updated_at: string;
}

/**
 * Fetches all sidebar shortcuts from DB. Used by the settings UI (all roles)
 * and the per-user lookup in `useSidebarShortcuts`.
 *
 * VIB-721c: Replaces previously hardcoded ADMIN_PL_SHORTCUTS / SALES_SHORTCUTS /
 * PRODUCTION_SHORTCUTS arrays. CRUD-UI lives in `SnarveierCard`.
 */
export function useAllSidebarShortcuts() {
  return useQuery({
    queryKey: ["sidebar-shortcuts"],
    queryFn: async (): Promise<DBSidebarShortcut[]> => {
      const { data, error } = await supabase
        .from("sidebar_shortcuts")
        .select("*")
        .order("role")
        .order("sort_order");
      if (error) throw error;
      return (data ?? []) as DBSidebarShortcut[];
    },
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Returns role-based sidebar shortcuts for the active (impersonated) user.
 * Filters on `enabled = true` + `role = activeUser.role`, sorted on sort_order.
 *
 * Returns an empty array while data is loading or when no active user exists.
 */
export function useSidebarShortcuts(): SidebarShortcut[] {
  const { activeUser } = useImpersonation();
  const { data } = useAllSidebarShortcuts();

  if (!activeUser || !data) return [];

  return data
    .filter((s) => s.role === activeUser.role && s.enabled)
    .sort((a, b) => a.sort_order - b.sort_order)
    .map((s) => ({
      key: s.id,
      label: s.label,
      path: s.path,
      icon: s.icon,
    }));
}
